import { Outlet } from "react-router-dom";
import NavBar from "./components/NavBar.jsx";

export default function App() {
  return (
    <div>
      <NavBar />
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <Outlet />
      </div>
    </div>
  );
}

function ProfilePage({ user, onLogin }) {
  return (
    <Page title="Profile">
      {user ? (
        <div className="space-y-2">
          <div><span className="font-medium">Name:</span> {user.name}</div>
          <div><span className="font-medium">Email:</span> {user.email}</div>
          <div>
            <span className="font-medium">Role:</span>{" "}
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[12px] uppercase">{user.role}</span>
          </div>
          <p className="text-gray-600 mt-4">You’re logged in via secure cookie.</p>
        </div>
      ) : (
        <LoginForm onSuccess={onLogin} />
      )}
    </Page>
  );
}

function LoginForm({ onSuccess }) {
  // Replaced with Turnstile-powered login form
  const [email, setEmail] = useState("admin@sterling.local");
  const [password, setPassword] = useState("admin123");
  const [tkn, setTkn] = useState("");
  const btnRef = useRef(null);

  const SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY;

  // Load Turnstile script once
  useEffect(() => {
    const id = "cf-turnstile-script";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.id = id;
    s.onload = () => {
      // render widget into #turnstile-container
      window.turnstile?.render("#turnstile-container", {
        sitekey: SITEKEY,
        callback: (token) => {
          setTkn(token);
          btnRef.current?.removeAttribute("disabled");
        },
        "error-callback": () => {
          setTkn("");
          btnRef.current?.setAttribute("disabled", "true");
          alert("Bot check failed. Please reload the page and try again.");
        },
      });
    };
    document.body.appendChild(s);
  }, [SITEKEY]);

  const onLogin = async () => {
    if (!tkn) { alert("Complete the bot check first."); return; }
    const res = await fetch(import.meta.env.VITE_API_BASE_URL + "/v1/auth/login", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, turnstileToken: tkn }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      alert(`Login failed: ${j.error || res.status}`);
      return;
    }
    // notify parent and navigate home
    try { onSuccess && onSuccess(); } catch (e) { /* ignore */ }
    window.location.href = "/";
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onLogin(); }} className="rounded-xl border bg-white p-4 space-y-3 max-w-md">
      <div className="font-semibold">Sign in</div>
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" className="w-full rounded-lg border px-3 py-2" required />
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" className="w-full rounded-lg border px-3 py-2" required />
      <div id="turnstile-container" className="mt-2"></div>
      <button ref={btnRef} type="submit" disabled={!tkn} className="rounded-xl bg-teal-600 text-white px-4 py-2 disabled:opacity-50">
        Sign in
      </button>
    </form>
  );
}

function AnnouncementsPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listAnnouncements()
      .then((res) => alive && (setItems(res.items || []), setErr("")))
      .catch(() => alive && setErr("Failed to load announcements."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const created = await createAnnouncement({ title, body });
      setItems((prev) => [created, ...prev]);
      setTitle(""); setBody("");
    } catch {
      alert("Create failed (admin only).");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Page title="Announcements">
      <div className="space-y-6">
        <RoleGate user={user} allow="admin" fallback={null}>
          <form onSubmit={handleCreate} className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
            <div className="font-semibold">Create announcement</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600" required />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Body"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 h-28 focus:outline-none focus:ring-2 focus:ring-teal-600" required />
            <button type="submit" disabled={creating}
              className="inline-flex items-center justify-center rounded-xl px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
              {creating ? "Publishing…" : "Publish"}
            </button>
          </form>
        </RoleGate>

        {loading ? <p className="text-gray-600">Loading…</p> : null}
        {err ? <p className="text-red-600">{err}</p> : null}
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{a.title}</h3>
                <span className="text-xs text-gray-500">{new Date(a.created_at).toLocaleString()}</span>
              </div>
              <p className="text-gray-700 mt-2 whitespace-pre-wrap">{a.body}</p>
              {a.author ? <p className="text-xs text-gray-500 mt-2">By {a.author.name}</p> : null}
            </div>
          ))}
          {items.length === 0 && !loading ? <p className="text-gray-600">No announcements yet.</p> : null}
        </div>
      </div>
    </Page>
  );
}

function DevPage() {
  const [h, setH] = useState(null);
  const [db, setDb] = useState(null);
  const [err, setErr] = useState("");

  async function load() {
    setErr("");
    try {
      const [hh, dd] = await Promise.all([health(), debugDb()]);
      setH(hh);
      setDb(dd);
    } catch (e) {
      setErr("Failed to reach Worker or DB.");
    }
  }

  useEffect(() => { load(); }, []);

  return (
    <Page title="DEV Debug">
      <div className="space-y-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Worker Health</h3>
            <button onClick={load} className="text-sm text-teal-700 hover:underline">Refresh</button>
          </div>
          {h ? (
            <div className="mt-2 text-sm">
              <div>ok: {String(h.ok)}</div>
              <div>time: {h.time}</div>
            </div>
          ) : <p className="text-gray-600 text-sm">Loading…</p>}
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <h3 className="font-semibold">D1 Status</h3>
          {db ? (
            db.connected ? (
              <div className="mt-2 text-sm">
                <div>connected: ✅</div>
                <div className="mt-2">
                  <div>users: {db.tables.users}</div>
                  <div>announcements: {db.tables.announcements}</div>
                  <div>ads: {db.tables.ads}</div>
                </div>
                <div className="text-xs text-gray-500 mt-2">as of {db.time}</div>
              </div>
            ) : (
              <div className="mt-2 text-sm text-red-600">connected: ❌ — {db.error || "Unknown error"}</div>
            )
          ) : <p className="text-gray-600 text-sm">Loading…</p>}
        </div>

        {err ? <p className="text-red-600 text-sm">{err}</p> : null}
      </div>
    </Page>
  );
}

// ===== Events UI =====
function EventsPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // create form (admin)
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");
  const [loc, setLoc] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listEvents()
      .then(res => alive && (setItems(res.items || []), setErr("")))
      .catch(() => alive && setErr("Failed to load events."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  async function doCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      await createEvent({ title, description: desc, starts_at: starts, ends_at: ends, location: loc });
      const res = await listEvents();
      setItems(res.items || []);
      setTitle(""); setDesc(""); setStarts(""); setEnds(""); setLoc("");
    } catch (e) {
      alert(e?.status === 403 ? "Admins only." : "Create failed.");
    } finally { setCreating(false); }
  }

  async function doRsvp(id, status) {
    try {
      await rsvpEvent(id, status);
      const res = await listEvents();
      setItems(res.items || []);
    } catch (e) {
      alert(e?.status === 401 ? "Please login first." : "RSVP failed.");
    }
  }

  return (
    <Page title="Events">
      {loading ? <p className="text-gray-600">Loading…</p> : null}
      {err ? <p className="text-red-600">{err}</p> : null}

      {isAdmin(user) && (
        <form onSubmit={doCreate} className="rounded-2xl border bg-white p-4 space-y-3 mb-6">
          <div className="font-semibold">Create event</div>
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Title" className="w-full rounded-lg border px-3 py-2" required />
          <textarea value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Description (optional)" className="w-full rounded-lg border px-3 py-2 h-24" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input type="datetime-local" value={starts} onChange={e=>setStarts(e.target.value)} className="rounded-lg border px-3 py-2" required />
            <input type="datetime-local" value={ends} onChange={e=>setEnds(e.target.value)} className="rounded-lg border px-3 py-2" required />
          </div>
          <input value={loc} onChange={e=>setLoc(e.target.value)} placeholder="Location (optional)" className="w-full rounded-lg border px-3 py-2" />
          <button disabled={creating} className="rounded-xl bg-teal-600 text-white px-4 py-2">
            {creating ? "Creating…" : "Create"}
          </button>
        </form>
      )}

      <div className="space-y-3">
        {items.map(ev => (
          <EventCard key={ev.id} ev={ev} onRsvp={doRsvp} user={user} />
        ))}
        {items.length === 0 && !loading ? <p className="text-gray-600">No events yet.</p> : null}
      </div>
    </Page>
  );
}

function EventCard({ ev, onRsvp, user }) {
  const [open, setOpen] = useState(false);
  const when = `${new Date(ev.starts_at).toLocaleString()} – ${new Date(ev.ends_at).toLocaleString()}`;
  return (
    <div className="rounded-2xl border bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{ev.title}</h3>
          <p className="text-sm text-gray-500">{when}{ev.location ? ` • ${ev.location}` : ""}</p>
        </div>
        <div className="text-sm text-gray-600">
          <span className="mr-3">✅ {ev.going}</span>
          <span className="mr-3">❓ {ev.maybe}</span>
          <span>✖️ {ev.no}</span>
        </div>
      </div>
      {ev.description ? <p className="text-gray-700 mt-2 whitespace-pre-wrap">{ev.description}</p> : null}
      <div className="mt-3 flex gap-2">
        <button onClick={()=>onRsvp(ev.id,"yes")} className="rounded-lg px-3 py-1 bg-teal-600 text-white">Yes</button>
        <button onClick={()=>onRsvp(ev.id,"maybe")} className="rounded-lg px-3 py-1 border">Maybe</button>
        <button onClick={()=>onRsvp(ev.id,"no")} className="rounded-lg px-3 py-1 border">No</button>
        {isAdmin(user) && (
          <div className="ml-auto flex items-center gap-2">
            <button onClick={()=>setOpen(!open)} className="text-sm text-teal-700 underline">View RSVPs</button>
            <DownloadRsvpsCsvButton eventId={ev.id} />
          </div>
        )}
      </div>
      {open && <RsvpList id={ev.id} />}
    </div>
  );
}

// uses `src/components/DownloadRsvpsCsvButton.jsx`

function RsvpList({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { listEventRsvps(id).then(r => setRows(r.items || [])); }, [id]);
  if (!rows) return <p className="text-sm text-gray-600 mt-2">Loading RSVPs…</p>;
  if (rows.length === 0) return <p className="text-sm text-gray-600 mt-2">No RSVPs yet.</p>;
  return (
    <div className="mt-3 text-sm rounded-lg border p-3">
      {rows.map((r, i) => (
        <div key={i} className="flex justify-between border-b last:border-0 py-1">
          <span>{r.name || r.user_id} <span className="text-gray-500">({r.email || "-"})</span></span>
          <span className="uppercase">{r.status}</span>
        </div>
      ))}
    </div>
  );
}

// ===== Admin UI =====
function AdminPage({ user }) {
  if (!user || user.role !== "admin") {
    return <Page title="Admin"><p className="text-gray-600">Admins only.</p></Page>;
  }
  const [tab, setTab] = useState("users");
  return (
    <Page title="Admin">
      <div className="mb-4 flex gap-2">
        {["users","content","system"].map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            className={"px-3 py-1 rounded-xl border " + (tab===t ? "bg-teal-600 text-white" : "bg-white")}>{t}</button>
        ))}
      </div>
      {tab==="users" ? <AdminUsers/> : null}
      {tab==="content" ? <AdminContent/> : null}
      {tab==="system" ? <AdminSystem/> : null}
    </Page>
  );
}

function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [role, setRole] = useState("staff"); const [password, setPassword] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => { load(); }, [page]);

  async function load() {
    setLoading(true);
    try {
      const r = await admin.usersPaged(page, limit);
      setRows(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      // leave rows as-is on error
    } finally { setLoading(false); }
  }

  async function createUser(e) { e.preventDefault();
    try {
      await admin.createUser({ email, name, role, password });
      await load();
      setEmail(""); setName(""); setPassword(""); setRole("staff");
    } catch (err) { alert("Create failed"); }
  }
  async function setRoleUser(id, roleVal) {
    try { await admin.setRole(id, roleVal); await load(); } catch { alert("Role update failed"); }
  }
  return (
    <div className="space-y-4">
      <form onSubmit={createUser} className="rounded-xl border bg-white p-3 grid gap-2 md:grid-cols-5">
        <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="email" className="border rounded px-2 py-1" required />
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="name" className="border rounded px-2 py-1" required />
        <select value={role} onChange={e=>setRole(e.target.value)} className="border rounded px-2 py-1">
          <option value="staff">staff</option><option value="admin">admin</option>
        </select>
        <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="password" className="border rounded px-2 py-1" required />
        <button className="rounded bg-teal-600 text-white px-3">Create</button>
      </form>
      {loading ? <p>Loading…</p> :
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr><th className="p-2 text-left">Email</th><th className="p-2">Name</th><th className="p-2">Role</th><th className="p-2">Created</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.map(u=>(
              <tr key={u.id} className="border-t">
                <td className="p-2">{u.email}</td>
                <td className="p-2">{u.name}</td>
                <td className="p-2">{u.role}</td>
                <td className="p-2 text-gray-500">{new Date(u.created_at).toLocaleString()}</td>
                <td className="p-2">
                  {u.role!=="admin" ? <button onClick={()=>setRoleUser(u.id,"admin")} className="text-xs underline">make admin</button> : <button onClick={()=>setRoleUser(u.id,"staff")} className="text-xs underline">make staff</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Page {page} / {Math.max(1, Math.ceil(total / limit))} • {total} users
        </div>
        <div className="flex gap-2">
          <button
            onClick={()=> setPage(Math.max(1, page-1))}
            className="rounded border px-3 py-1 disabled:opacity-50"
            disabled={page<=1}
          >Prev</button>
          <button
            onClick={()=> setPage(page+1)}
            className="rounded border px-3 py-1 disabled:opacity-50"
            disabled={page >= Math.ceil(total/limit)}
          >Next</button>
          <button
            onClick={()=> download(admin.usersCsvUrl(), "users.csv")}
            className="rounded bg-gray-900 text-white px-3 py-1"
          >Export CSV</button>
        </div>
      </div>
    </div>
  );
}

function AdminContent() {
  const [ann,setAnn]=useState([]); const [ads,setAds]=useState([]); const [evs,setEvs]=useState([]);
  useEffect(()=>{
    Promise.all([
      api("/v1/announcements"),
      api("/v1/ads/active?placement=bottom"), // just to show it loads
      api("/v1/events")
    ]).then(([a,adsRes,e])=>{
      setAnn(a.items||[]);
      setAds(adsRes || []);
      setEvs(e.items||[]);
    }).catch(()=>{});
  },[]);
  async function delAnn(id){ if(!confirm("Delete announcement?"))return; await admin.delAnnouncement(id); setAnn(ann.filter(x=>x.id!==id)); }
  async function delAd(id){ if(!confirm("Delete ad?"))return; await admin.delAd(id); setAds(ads.filter(x=>x.id!==id)); }
  async function delEv(id){ if(!confirm("Delete event?"))return; await admin.delEvent(id); setEvs(evs.filter(x=>x.id!==id)); }
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="rounded-xl border p-3">
        <div className="font-semibold mb-2">Announcements</div>
        {ann.map(a=>(
          <div key={a.id} className="border rounded p-2 mb-2">
            <div className="font-medium">{a.title}</div>
            <button onClick={()=>delAnn(a.id)} className="text-xs text-red-600 underline mt-1">Delete</button>
          </div>
        ))}
        {ann.length===0?<p className="text-sm text-gray-500">None</p>:null}
      </div>
      <div className="rounded-xl border p-3">
        <div className="font-semibold mb-2">Ads (bottom)</div>
        {ads.map(ad=>(
          <div key={ad.id} className="border rounded p-2 mb-2">
            <div className="font-medium">{ad.title}</div>
            <button onClick={()=>delAd(ad.id)} className="text-xs text-red-600 underline mt-1">Delete</button>
          </div>
        ))}
        {ads.length===0?<p className="text-sm text-gray-500">None</p>:null}
      </div>
      <div className="rounded-xl border p-3">
        <div className="font-semibold mb-2">Events</div>
        {evs.map(ev=>(
          <div key={ev.id} className="border rounded p-2 mb-2">
            <div className="font-medium">{ev.title}</div>
            <button onClick={()=>delEv(ev.id)} className="text-xs text-red-600 underline mt-1">Delete</button>
          </div>
        ))}
        {evs.length===0?<p className="text-sm text-gray-500">None</p>:null}
      </div>
    </div>
  );
}

function AdminSystem() {
  const [db,setDb]=useState(null); const [err,setErr]=useState("");
  async function load(){
    try {
      // show live counts via a public endpoint if you added one; otherwise reuse /v1/health then /v1/dev/db when DEV_TOOLS=1
      const h = await api("/v1/health");
      setDb(h);
    } catch(e){ setErr("Failed to load."); }
  }
  return (
    <div className="space-y-3">
      <button onClick={load} className="rounded bg-gray-900 text-white px-3 py-1">Check health</button>
      {err ? <p className="text-red-600">{err}</p>: null}
      {db ? <pre className="bg-gray-100 rounded p-2 text-xs">{JSON.stringify(db,null,2)}</pre> : <p className="text-gray-500 text-sm">Click “Check health”.</p>}
    </div>
  );
}
